/**
 * Logger utility with configurable log levels
 * Supports different log levels: debug, info, warn, error
 * Only logs in development mode by default (except errors)
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4,
};

// Get log level from environment variable or default to INFO in production, DEBUG in development
const getLogLevel = () => {
  const envLevel = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();
  if (envLevel && LOG_LEVELS[envLevel] !== undefined) {
    return LOG_LEVELS[envLevel];
  }
  // Default: DEBUG in development, WARN in production
  return process.env.NODE_ENV === 'development' ? LOG_LEVELS.DEBUG : LOG_LEVELS.WARN;
};

const currentLogLevel = getLogLevel();

/**
 * Logger class with configurable log levels
 */
class Logger {
  constructor(module = 'App') {
    this.module = module;
  }

  /**
   * Check if a log level should be output
   */
  shouldLog(level) {
    return level >= currentLogLevel;
  }

  /**
   * Format log message with module name
   */
  formatMessage(level, ...args) {
    const timestamp = new Date().toISOString();
    const levelName = Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === level);
    return [`[${timestamp}] [${levelName}] [${this.module}]`, ...args];
  }

  /**
   * Debug logs - only in development
   */
  debug(...args) {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      // eslint-disable-next-line no-console
      console.log(...this.formatMessage(LOG_LEVELS.DEBUG, ...args));
    }
  }

  /**
   * Info logs - development and production (if level allows)
   */
  info(...args) {
    if (this.shouldLog(LOG_LEVELS.INFO)) {
      // eslint-disable-next-line no-console
      console.info(...this.formatMessage(LOG_LEVELS.INFO, ...args));
    }
  }

  /**
   * Warning logs - always shown unless log level is ERROR or NONE
   */
  warn(...args) {
    if (this.shouldLog(LOG_LEVELS.WARN)) {
      // eslint-disable-next-line no-console
      console.warn(...this.formatMessage(LOG_LEVELS.WARN, ...args));
    }
  }

  /**
   * Error logs - always shown unless log level is NONE
   */
  error(...args) {
    if (this.shouldLog(LOG_LEVELS.ERROR)) {
      // eslint-disable-next-line no-console
      console.error(...this.formatMessage(LOG_LEVELS.ERROR, ...args));
    }
  }
}

/**
 * Create a logger instance for a specific module
 * @param {string} module - Module name for context
 * @returns {Logger} Logger instance
 */
export const createLogger = (module) => new Logger(module);

/**
 * Default logger instance
 */
export const logger = createLogger('App');

export default logger;

